import { AudioQueueItem } from '@/shared-survey';

/**
 * Constants for audio controller functionality
 */
const USER_INTERACTION_EVENTS = ['click', 'touchstart', 'keydown'] as const;

interface AudioUpdate {
  status: 'ready' | 'playing' | 'error';
  shouldInterrupt?: boolean;
}

export function syncAudioQueueOnModeTransition({
  responseMode,
  isAiVoiceEnabled,
  playbackPaused,
  prevResponseModeRef,
  currentPlayingRef,
  queueRef,
  updateAudio,
}: {
  responseMode: string;
  isAiVoiceEnabled: boolean;
  playbackPaused: boolean;
  prevResponseModeRef: React.RefObject<string>;
  currentPlayingRef: React.RefObject<string | null>;
  queueRef: React.RefObject<AudioQueueItem[]>;
  updateAudio: (params: { messageId: string; updates: AudioUpdate }) => void;
}): void {
  const wasVoice = prevResponseModeRef.current === 'voice';
  const isVoice = responseMode === 'voice';
  prevResponseModeRef.current = responseMode;

  if (
    !wasVoice &&
    isVoice &&
    isAiVoiceEnabled &&
    !playbackPaused &&
    !currentPlayingRef.current
  ) {
    const lastCompleted = queueRef.current
      .toReversed()
      .find((item) => item.status === 'completed' && item.audioUrl);

    if (lastCompleted) {
      updateAudio({
        messageId: lastCompleted.messageId,
        updates: { status: 'ready', shouldInterrupt: true },
      });
    }
  }
}

export function isInterruptingAudioLoading(queue: AudioQueueItem[]): boolean {
  return queue.some(
    (item) => item.status === 'loading' && item.shouldInterrupt
  );
}

export function findInterruptingAudio(
  queue: AudioQueueItem[],
  failedAttempts: Set<string>
): AudioQueueItem | undefined {
  return queue.find(
    (item) =>
      item.status === 'ready' &&
      item.audioUrl &&
      item.shouldInterrupt &&
      !failedAttempts.has(item.messageId)
  );
}

export function findNextReadyAudio(
  queue: AudioQueueItem[],
  failedAttempts: Set<string>
): AudioQueueItem | undefined {
  return queue.find(
    (item) =>
      item.status === 'ready' &&
      item.audioUrl &&
      !failedAttempts.has(item.messageId)
  );
}

export function selectNextAudioToPlay({
  queue,
  currentlyPlaying,
  failedAttempts,
}: {
  queue: AudioQueueItem[];
  currentlyPlaying: string | null;
  failedAttempts: Set<string>;
}): AudioQueueItem | undefined {
  const interruptingAudio = findInterruptingAudio(queue, failedAttempts);
  if (interruptingAudio) return interruptingAudio;
  if (currentlyPlaying) return undefined;
  return findNextReadyAudio(queue, failedAttempts);
}

export function driveAudioElementFromQueue({
  audioRef,
  isAiVoiceEnabled,
  responseMode,
  playbackPaused,
  hasInteracted,
  setHasInteracted,
  failedAutoplayAttempts,
  currentPlayingRef,
  queue,
  updateAudio,
  markCompleted,
  setCurrentPlayingId,
  currentPlayingIdFromAtom: _currentPlayingIdFromAtom,
}: {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  isAiVoiceEnabled: boolean;
  responseMode: string;
  playbackPaused: boolean;
  hasInteracted: boolean;
  setHasInteracted: (value: boolean) => void;
  failedAutoplayAttempts: React.RefObject<Set<string>>;
  currentPlayingRef: React.RefObject<string | null>;
  queue: AudioQueueItem[];
  updateAudio: (params: { messageId: string; updates: AudioUpdate }) => void;
  markCompleted: (messageId: string) => void;
  setCurrentPlayingId: (id: string | null) => void;
  /** Unused inside — only here so the calling effect can list it as a re-run trigger. */
  currentPlayingIdFromAtom: string | null;
}): void {
  const audio = audioRef.current;

  if (!audio) return;

  if (!isAiVoiceEnabled || responseMode !== 'voice') {
    const playingId = currentPlayingRef.current;
    if (playingId && audio) {
      audio.pause();
      audio.currentTime = 0;
      markCompleted(playingId);
      currentPlayingRef.current = null;
      setCurrentPlayingId(null);
    }
    return;
  }

  if (playbackPaused) {
    const currentlyPlaying = currentPlayingRef.current;
    if (currentlyPlaying) {
      audio.pause();
      audio.currentTime = 0;
      markCompleted(currentlyPlaying);
      currentPlayingRef.current = null;
      setCurrentPlayingId(null);
    }
    audio.volume = 0;
    return;
  }

  audio.volume = 1;

  if (hasInteracted && failedAutoplayAttempts.current.size > 0) {
    failedAutoplayAttempts.current.clear();
  }

  const currentlyPlaying = currentPlayingRef.current;

  if (currentlyPlaying && isInterruptingAudioLoading(queue)) {
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    markCompleted(currentlyPlaying);
    currentPlayingRef.current = null;
    setCurrentPlayingId(null);
    return;
  }

  const interruptingAudio = findInterruptingAudio(
    queue,
    failedAutoplayAttempts.current
  );
  const audioToPlay = selectNextAudioToPlay({
    queue,
    currentlyPlaying,
    failedAttempts: failedAutoplayAttempts.current,
  });

  if (audioToPlay && audioToPlay.audioUrl) {
    if (currentlyPlaying && interruptingAudio) {
      audio.pause();
      audio.currentTime = 0;
      markCompleted(currentlyPlaying);
    }

    updateAudio({
      messageId: audioToPlay.messageId,
      updates: { status: 'playing' },
    });

    currentPlayingRef.current = audioToPlay.messageId;
    setCurrentPlayingId(audioToPlay.messageId);

    audio.src = audioToPlay.audioUrl;
    audio.load();

    audio
      .play()
      .then(() => {
        setHasInteracted(true);

        if (audio.muted) {
          audio.muted = false;
        }
      })
      .catch((err) => {
        if (err.name === 'NotAllowedError' && !hasInteracted) {
          audio.muted = true;
          audio
            .play()
            .then(() => {
              setHasInteracted(true);
              const unmute = () => {
                if (audio.muted && audioRef.current) {
                  audio.muted = false;
                }
              };
              setupUserInteractionListeners(unmute, { once: true });
            })
            .catch(() => {
              if (currentPlayingRef.current === audioToPlay.messageId) {
                failedAutoplayAttempts.current.add(audioToPlay.messageId);
                updateAudio({
                  messageId: audioToPlay.messageId,
                  updates: { status: 'ready' },
                });
                currentPlayingRef.current = null;
                setCurrentPlayingId(null);

                setupUserInteractionListeners(
                  () => {
                    setHasInteracted(true);
                  },
                  { once: true }
                );
              }
            });
        } else if (currentPlayingRef.current === audioToPlay.messageId) {
          updateAudio({
            messageId: audioToPlay.messageId,
            updates: { status: 'error' },
          });
          currentPlayingRef.current = null;
          setCurrentPlayingId(null);
        }
      });
  }
}

/**
 * Sets up user interaction listeners and returns a cleanup function.
 * Pure function that handles the common pattern of listening to multiple interaction events.
 */
function setupUserInteractionListeners(
  callback: () => void,
  options?: { once?: boolean }
): () => void {
  const { once = false } = options ?? {};

  USER_INTERACTION_EVENTS.forEach((eventType) => {
    window.addEventListener(eventType, callback, { once });
  });

  return () => {
    USER_INTERACTION_EVENTS.forEach((eventType) => {
      window.removeEventListener(eventType, callback);
    });
  };
}
